import React, { useCallback, useMemo, useState } from 'react';

import { useIntl } from 'react-intl';
import styled from 'styled-components';

import { ICartEntry, IHistoricalOrder } from '@rbi-ctg/menu';
import { ActionButton } from 'components/action-button';
import { LoadingAnimation } from 'components/loading-animation';
import { ModalOrderUnavailable } from 'components/modal-order-unavailable';
import { usePosVendor } from 'hooks/menu/use-pos-vendor';
import { useAddToCartRecentItem } from 'hooks/use-add-to-cart-recent-item';
import { useEffectOnce } from 'hooks/use-effect-once';
import { UserOrdersDefaultLimit, useUserOrders } from 'hooks/use-user-orders';
import { useAuthContext } from 'state/auth';
import { useCdpContext } from 'state/cdp';
import { CustomEventNames, EventTypes } from 'state/cdp/constants';
import { useMenuContext } from 'state/menu';
import { useOrderContext } from 'state/order';
import { useStoreContext } from 'state/store';
import { isCartEntryInMenu } from 'utils/cart';
import { maybeMapCartEntryToMenuObjectIdentifier } from 'utils/menu';

import { ErrorState, LoggedOutEmptyState, NoRecentOrdersEmptyState } from './empty-state';
import { RecentOrderCardGroup } from './recent-order-card-group';

const PageContainer = styled.div`
  margin-block-end: 110px;
`;

const LoadingContainer = styled.div`
  margin-block-start: 2rem;
`;

const LoadMoreButton = styled(ActionButton)`
  margin: 2rem auto;
`;

export const RecentOrderCardList: React.FC = () => {
  const { formatMessage } = useIntl();
  const [selectionUnavailable, setSelectionUnavailable] = useState<ICartEntry | null>(null);
  const { trackEvent } = useCdpContext();
  const { vendor } = usePosVendor();
  const { checkItemAvailability } = useMenuContext();
  const { isAuthenticated } = useAuthContext();
  const [fetchingMore, setFetchingMore] = useState(false);
  const { isStoreOpenAndAvailable, prices, store } = useStoreContext();
  const {
    recent: { pendingRecentItem, setPendingRecentItem },
  } = useOrderContext();
  const { addToCart } = useAddToCartRecentItem({ cartEntry: pendingRecentItem });

  const { loading, data, error, count, resumeAfter, fetchMore } = useUserOrders({
    variables: {
      limit: UserOrdersDefaultLimit,
    },
  });

  const orders = (data?.userOrders?.orders || []) as unknown as IHistoricalOrder[];
  const handleLoadMoreClick = async () => {
    trackEvent({
      name: CustomEventNames.LOAD_MORE,
      type: EventTypes.Other,
      attributes: {
        'Source Page': '/recentitems',
      },
    });
    setFetchingMore(true);
    try {
      await fetchMore({
        variables: {
          resumeAfter,
        },
        updateQuery: (prev, { fetchMoreResult }) => {
          if (!fetchMoreResult) {
            return prev;
          }
          return {
            ...fetchMoreResult,
            userOrders: {
              ...fetchMoreResult.userOrders,
              count: prev.userOrders.count + fetchMoreResult.userOrders.count,
              orders: prev.userOrders.orders?.concat(fetchMoreResult.userOrders.orders) ?? [],
            },
          };
        },
      });
      setFetchingMore(false);
    } catch {
      setFetchingMore(false);
    }
  };

  const addToCartIfAvailable = useCallback(
    async (cartEntry: ICartEntry) => {
      const itemId = maybeMapCartEntryToMenuObjectIdentifier(cartEntry);

      if (!itemId) {
        setSelectionUnavailable(cartEntry);
        return;
      }

      const { availabilityStatus } = await checkItemAvailability({
        vendor,
        storeNumber: store.number ?? '',
        itemId,
      });

      if (availabilityStatus !== 'AVAILABLE') {
        setSelectionUnavailable(cartEntry);
      } else {
        addToCart();
      }
    },
    [addToCart, checkItemAvailability, store.number, store.restaurantPosData, vendor]
  );

  useEffectOnce(() => {
    if (isStoreOpenAndAvailable && pendingRecentItem && prices) {
      setPendingRecentItem(null);
      addToCartIfAvailable(pendingRecentItem);
    }
  });

  const reorderableOrders = useMemo(
    () =>
      orders.reduce((ordersAccum: IHistoricalOrder[], order) => {
        const filteredCartEntries = order.cart.reorderCartEntries?.filter(
          cartEntry => isCartEntryInMenu(cartEntry) && !cartEntry.isDonation
        );

        if (filteredCartEntries?.length) {
          ordersAccum.push({
            ...order,
            cart: {
              ...order.cart,
              reorderCartEntries: filteredCartEntries,
            },
          });
        }

        return ordersAccum;
      }, []),
    [orders]
  );

  if (loading) {
    return (
      <LoadingContainer>
        <LoadingAnimation fillColor={Styles.color.primary} />
      </LoadingContainer>
    );
  }

  if (!isAuthenticated()) {
    return (
      <PageContainer>
        <LoggedOutEmptyState />
      </PageContainer>
    );
  }

  if (error) {
    return (
      <PageContainer>
        <ErrorState />
      </PageContainer>
    );
  }

  if (!reorderableOrders.length && !resumeAfter) {
    return (
      <PageContainer>
        <NoRecentOrdersEmptyState />
      </PageContainer>
    );
  }

  return (
    <PageContainer>
      {reorderableOrders.map(order => (
        <RecentOrderCardGroup key={order?.rbiOrderId} order={order} />
      ))}
      {count > 0 && resumeAfter && (
        <LoadMoreButton
          data-testid="load-more-button"
          onClick={handleLoadMoreClick}
          isLoading={fetchingMore}
        >
          {formatMessage({ id: 'loadMore' })}
        </LoadMoreButton>
      )}
      {selectionUnavailable && (
        <ModalOrderUnavailable
          items={[selectionUnavailable]}
          onDismiss={() => setSelectionUnavailable(null)}
        />
      )}
    </PageContainer>
  );
};
